import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Camera, Check, RotateCcw, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';

const fmtTime = (t) => {
  const s = Math.max(0, Math.floor(t || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Main-area freeze-frame capture stage — takes over the canvas area while
 * a Thumbnail design hasn't captured its frame yet. Uses only native
 * <video>/<canvas> APIs (section 3) — no new dependency. `toDataURL` is
 * wrapped in try/catch: a cross-origin/tainted source throws there, not
 * on drawImage, so that is exactly where we report the real limitation
 * instead of a false "captured" state.
 */
export function ThumbnailCaptureStage({
  streamUrl, streamError, clipRange, videoRef, sourceSelected,
  currentTime, duration, isPlaying, onTogglePlay, onSeek, onStepFrame,
  capturedFrameUrl, captureError, onCapture, onRetake, onUseFrame,
}) {
  const canvasRef = useRef(null);

  const handleCaptureClick = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      onCapture(dataUrl);
    } catch {
      onCapture(null, "This video's pixels can't be read back in the browser (cross-origin restriction on the source file) — frame capture isn't available for this video. Try a different clip or video.");
    }
  };

  if (streamError) {
    return (
      <div className="flex-1 flex items-center justify-center text-center text-sm text-amber-300 px-6" data-testid="thumbnail-capture-stream-error">{streamError}</div>
    );
  }

  if (capturedFrameUrl) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3" data-testid="thumbnail-captured-preview">
        <img src={capturedFrameUrl} alt="Captured frame" className="max-h-[80%] max-w-[90%] rounded-lg border border-white/10 object-contain" data-testid="thumbnail-captured-frame-img" />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-white/10" onClick={onRetake} data-testid="thumbnail-retake-button"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Retake</Button>
          <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" onClick={onUseFrame} data-testid="thumbnail-use-frame-button"><Check className="h-3.5 w-3.5 mr-1.5" />Use Captured Frame as Thumbnail</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="thumbnail-capture-stage">
      <canvas ref={canvasRef} className="hidden" />
      <div className="flex-1 min-h-0 bg-black rounded-lg overflow-hidden flex items-center justify-center relative">
        {streamUrl ? (
          <video ref={videoRef} src={streamUrl} className="max-h-full max-w-full" data-testid="thumbnail-capture-video" />
        ) : (
          <p className="text-xs text-[#8A93A6] px-6 text-center" data-testid="thumbnail-capture-no-stream">
            {sourceSelected ? "This source doesn't have a playable video yet — it may still be processing, or has no uploaded file." : 'Pick a video or clip on the left to start capturing a frame.'}
          </p>
        )}
        {clipRange && (
          <div className="absolute top-2 left-2 bg-black/60 text-[10px] text-[#8A93A6] px-2 py-1 rounded-md">Clip range {fmtTime(clipRange.start)}–{fmtTime(clipRange.end)}</div>
        )}
      </div>
      {streamUrl && (
        <div className="pt-2 space-y-2 shrink-0">
          {captureError && (
            <p className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md p-2 flex items-start gap-1.5" data-testid="thumbnail-capture-error">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{captureError}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button onClick={onTogglePlay} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0" data-testid="thumbnail-capture-playpause">
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
            </button>
            <button onClick={() => onStepFrame(-1)} className="h-7 w-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center shrink-0 text-[#8A93A6] hover:text-white" title="Previous frame (~1/30s)" data-testid="thumbnail-capture-step-back"><ChevronLeft className="h-4 w-4" /></button>
            <input type="range" min={0} max={duration || 0} step={0.03} value={currentTime} onChange={(e) => onSeek(Number(e.target.value))} className="flex-1 accent-[#3A7BFF]" data-testid="thumbnail-capture-scrubber" />
            <button onClick={() => onStepFrame(1)} className="h-7 w-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center shrink-0 text-[#8A93A6] hover:text-white" title="Next frame (~1/30s)" data-testid="thumbnail-capture-step-forward"><ChevronRight className="h-4 w-4" /></button>
            <span className="text-[11px] text-[#8A93A6] tabular-nums shrink-0">{fmtTime(currentTime)} / {fmtTime(duration)}</span>
          </div>
          <Button size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" onClick={handleCaptureClick} data-testid="thumbnail-capture-frame-button">
            <Camera className="h-4 w-4 mr-2" />Capture Frame
          </Button>
        </div>
      )}
    </div>
  );
}
